/**
 * useSaveToGmailDrafts — place a prepared `gmail_send` proposal action into
 * the user's Gmail drafts via the backend's draft-placement endpoint
 * (`apps/email/views/gmail_draft_placement_views.py::GmailDraftPlacementView`).
 *
 * The endpoint itself resolves every outcome server-side (drafted, blocked by
 * a missing `gmail.compose` grant, or genuinely disabled) and reports it as a
 * `status` on a 200 response — it never 4xx/5xxs for those expected cases. So
 * this hook's job is purely to route each `status` to the right client
 * behavior, not to re-derive eligibility itself:
 *
 *   - `drafted` — the draft was created; show a success toast.
 *   - `fallback_deep_link` — placement isn't available right now (e.g. the
 *     `gmail.compose` scope hasn't been granted); open the returned Gmail
 *     compose deep-link in a new tab so the user can still send by hand.
 *   - `feature_disabled` / anything else unrecognized — surface the same
 *     "can't place in Gmail drafts" copy used on the scope-permissions screen
 *     (`ScopePermissionsRequired.tsx`) and log a client-side error so a
 *     silent failure is never invisible.
 *
 * A network/HTTP failure (the request itself rejecting) gets the same error
 * treatment.
 */

import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';

import { apiPost } from '@/lib/http/client';
import { logger } from '@/lib/logger';

/** Mirrors `GmailDraftPlacementResponseSerializer`. */
export interface GmailDraftPlacementResult {
  status: 'drafted' | 'fallback_deep_link' | 'feature_disabled';
  draft_id: string | null;
  deep_link: string | null;
  message: string;
}

/**
 * Reuses the exact consequence copy `ScopePermissionsRequired.tsx` shows for
 * a missing `gmail.compose` grant, so the same failure reads identically
 * wherever the user encounters it.
 */
export const GMAIL_DRAFTS_UNAVAILABLE_MESSAGE =
  "Prepared emails can't be placed in your Gmail drafts";

async function placeInGmailDrafts(
  orgId: number,
  actionId: number
): Promise<GmailDraftPlacementResult> {
  return apiPost<GmailDraftPlacementResult>(
    `/api/orgs/${orgId}/email/gmail/draft/`,
    { action_id: actionId }
  );
}

export interface UseSaveToGmailDraftsResult {
  /** Trigger placement for the given gmail_send action id. */
  saveToGmailDrafts: (actionId: number) => void;
  /** True while the placement request is in flight. */
  isSaving: boolean;
}

export function useSaveToGmailDrafts(
  orgId: number
): UseSaveToGmailDraftsResult {
  const mutation = useMutation({
    mutationFn: (actionId: number) => placeInGmailDrafts(orgId, actionId),
    onSuccess: result => {
      if (result.status === 'drafted') {
        toast.success(result.message || 'Saved to Gmail Drafts');
        return;
      }
      if (result.status === 'fallback_deep_link') {
        if (result.deep_link) {
          window.open(result.deep_link, '_blank', 'noopener,noreferrer');
        }
        toast.success(result.message || 'Opened in Gmail');
        return;
      }
      logger.error(
        'Gmail draft placement did not draft or fall back',
        undefined,
        { orgId, status: result.status }
      );
      toast.error(GMAIL_DRAFTS_UNAVAILABLE_MESSAGE);
    },
    onError: error => {
      logger.error(
        'Gmail draft placement request failed',
        error instanceof Error ? error : undefined,
        { orgId }
      );
      toast.error(GMAIL_DRAFTS_UNAVAILABLE_MESSAGE);
    },
  });

  return {
    saveToGmailDrafts: mutation.mutate,
    isSaving: mutation.isPending,
  };
}
